/**
 * VoiceQAPanel — Plan 6 (Voice Q&A)
 *
 * Lets the user ask questions about the transcript by voice or text.
 * - Primary: Web Speech API (SpeechRecognition) for voice input
 * - Fallback: text input when browser doesn't support SpeechRecognition
 * - Sends POST /api/voice { action: 'voice-qa', question, transcript, history }
 * - Renders conversation history with AudioPlayer for each answer
 */

import { useState, useRef, useCallback } from 'react';
import { AudioPlayer } from './AudioPlayer';

interface QAMessage {
  role: 'user' | 'assistant';
  text: string;
  audioBase64?: string;
}

interface VoiceQAPanelProps {
  /** Full transcript text — passed to the API for grounding */
  transcript: string;
}

// Web Speech API type shim (not in TS lib by default)
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
}
declare class SpeechRecognition extends EventTarget {
  lang: string;
  interimResults: boolean;
  maxAlternatives: number;
  onresult: ((e: SpeechRecognitionEvent) => void) | null;
  onerror: ((e: Event) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
}

function getSpeechRecognition(): typeof SpeechRecognition | null {
  if (typeof window === 'undefined') return null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition || null;
}

export function VoiceQAPanel({ transcript }: VoiceQAPanelProps) {
  const [history, setHistory] = useState<QAMessage[]>([]);
  const [question, setQuestion] = useState('');
  const [listening, setListening] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const recognitionRef = useRef<InstanceType<typeof SpeechRecognition> | null>(null);
  const SpeechRecognitionClass = getSpeechRecognition();
  const supportsVoice = !!SpeechRecognitionClass;

  const startListening = useCallback(() => {
    if (!SpeechRecognitionClass) return;
    setError(null);

    const recognition = new SpeechRecognitionClass();
    recognition.lang = 'en-GB';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onresult = (e: SpeechRecognitionEvent) => {
      const transcript = e.results[0]?.[0]?.transcript ?? '';
      setQuestion(transcript);
    };
    recognition.onerror = () => {
      setListening(false);
      setError('Voice input failed. Please type your question.');
    };
    recognition.onend = () => setListening(false);

    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  }, [SpeechRecognitionClass]);

  const stopListening = useCallback(() => {
    recognitionRef.current?.stop();
    setListening(false);
  }, []);

  const submitQuestion = useCallback(async (q: string) => {
    const trimmed = q.trim();
    if (!trimmed || loading) return;

    setLoading(true);
    setError(null);
    setQuestion('');

    const userMsg: QAMessage = { role: 'user', text: trimmed };
    setHistory(prev => [...prev, userMsg]);

    try {
      const res = await fetch('/api/voice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'voice-qa',
          question: trimmed,
          transcript,
          // Pass history (without audio — API doesn't need it)
          history: history.map(m => ({ role: m.role === 'assistant' ? 'assistant' : 'user', text: m.text })),
        }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error || `HTTP ${res.status}`);
      }

      const data = await res.json() as { answer: string; audioBase64: string };
      const assistantMsg: QAMessage = {
        role: 'assistant',
        text: data.answer,
        audioBase64: data.audioBase64,
      };
      setHistory(prev => [...prev, assistantMsg]);
    } catch (err) {
      setError((err as Error).message || 'Failed to get answer');
      // Remove the optimistic user message on error
      setHistory(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  }, [loading, transcript, history]);

  if (!transcript?.trim()) return null;

  return (
    <div className="mt-6 border border-[#00d4b8]/20 rounded-2xl overflow-hidden bg-[#00d4b8]/3">
      {/* Header */}
      <div className="px-4 py-3 border-b border-[#00d4b8]/10 flex items-center gap-2">
        <span className="text-base">🎙️</span>
        <span className="text-[10px] font-black text-[#00d4b8] uppercase tracking-[0.4em]">Voice Q&A</span>
        <span className="text-[10px] text-slate-400 ml-1">— Ask questions about this transcript</span>
      </div>

      {/* Conversation history */}
      {history.length > 0 && (
        <div className="px-4 py-3 space-y-3 max-h-72 overflow-y-auto">
          {history.map((msg, i) => (
            <div key={i} className={`flex flex-col gap-1 ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
              <div
                className={`max-w-[85%] px-3 py-2 rounded-xl text-[12px] leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-[#00d4b8] text-white'
                    : 'bg-white border border-[#00d4b8]/20 text-[#1a2332]'
                }`}
              >
                {msg.text}
              </div>
              {msg.role === 'assistant' && msg.audioBase64 && (
                <div className="max-w-[85%] w-full">
                  <AudioPlayer audioBase64={msg.audioBase64} />
                </div>
              )}
            </div>
          ))}
          {loading && (
            <div className="flex items-center gap-2 text-[11px] text-slate-400 italic">
              <span className="w-3 h-3 border border-slate-300 border-t-[#00d4b8] rounded-full animate-spin" />
              Thinking…
            </div>
          )}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="px-4 py-2 text-[11px] text-red-500 bg-red-50 border-t border-red-100">
          {error}
        </div>
      )}

      {/* Input row */}
      <div className="px-4 py-3 border-t border-[#00d4b8]/10 flex items-center gap-2">
        {supportsVoice && (
          <button
            onClick={listening ? stopListening : startListening}
            disabled={loading}
            className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all border font-bold text-sm
              ${listening
                ? 'bg-red-500 border-red-500 text-white animate-pulse'
                : 'bg-white border-[#00d4b8]/40 text-[#00d4b8] hover:bg-[#00d4b8] hover:text-white'
              } disabled:opacity-40 disabled:cursor-not-allowed`}
            title={listening ? 'Stop listening' : 'Ask by voice'}
            aria-label={listening ? 'Stop recording' : 'Start voice input'}
          >
            🎤
          </button>
        )}

        <input
          type="text"
          value={question}
          onChange={e => setQuestion(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && submitQuestion(question)}
          placeholder={supportsVoice ? 'Or type your question…' : 'Ask a question about the transcript…'}
          disabled={loading}
          className="flex-1 min-w-0 px-3 py-1.5 text-[12px] bg-white border border-[#00d4b8]/20 rounded-lg focus:outline-none focus:border-[#00d4b8] text-[#1a2332] placeholder:text-slate-300 disabled:opacity-50"
        />

        <button
          onClick={() => submitQuestion(question)}
          disabled={!question.trim() || loading}
          className="shrink-0 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all bg-[#00d4b8] text-white hover:bg-[#00b8a0] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Ask
        </button>
      </div>
    </div>
  );
}
